import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useBeforeUnload } from "react-use";

import Stepper from "@/components/stepper/Stepper";
import { Loadable } from "@/components";
import { useGetIntegrationRequestQuery } from "@/api/productIntegration";

import CustomerAndProductDetails from "./CustomerAndProductDetails";
import MerchantConfiguration from "./MerchantConfiguration";
import TransactionCharge from "./TransactionCharge";
import UploadPaymentForm from "./UploadPaymentForm";
import IntegrationSummary from "./IntegrationSummary";
import {
    ProductIntegrationContext,
    transformIntegrationRequestToCreateValues,
} from "../productIntegrationUtils";
import { ProductIntegrationWrapper } from "./productIntegrationStyles";

export default function ProductIntegration() {
    const [searchParams] = useSearchParams();
    const requestId = searchParams.get("requestId");
    const mode = searchParams.get("mode");
    const isEditMode = mode === "edit" && Boolean(requestId);
    const [values, setValues] = useState({});
    const [warnOnUnload, toggleWarnOnUnload] = useState(false);
    const [hasHydratedValues, setHasHydratedValues] = useState(false);

    const {
        data: integrationRequest,
        isLoading: isLoadingIntegrationRequest,
        error: integrationRequestError,
        refetch: refetchIntegrationRequest,
    } = useGetIntegrationRequestQuery(requestId, { skip: !isEditMode });

    useBeforeUnload(warnOnUnload, "Your changes might be discarded");

    const updateValues = (valueArg = {}) =>
        setValues((prevValues) => ({ ...prevValues, ...valueArg }));

    const requestValues = useMemo(
        () => transformIntegrationRequestToCreateValues(integrationRequest),
        [integrationRequest],
    );
    const shouldWaitForHydration = isEditMode && !hasHydratedValues;

    useEffect(() => {
        if (!isEditMode || !integrationRequest || hasHydratedValues) return;
        setValues((prevValues) => ({
            ...requestValues,
            ...prevValues,
        }));
        setHasHydratedValues(true);
    }, [hasHydratedValues, integrationRequest, isEditMode, requestValues]);

    return (
        <Loadable
            isLoading={(isEditMode && isLoadingIntegrationRequest) || shouldWaitForHydration}
            error={isEditMode ? integrationRequestError : null}
            retry={refetchIntegrationRequest}
            height="70vh"
        >
            <ProductIntegrationContext.Provider
                value={{
                    values,
                    addToValues: updateValues,
                    setValues,
                    warnOnUnload,
                    toggleWarnOnUnload,
                    isEditMode,
                    requestId,
                    requestStatus: integrationRequest?.status,
                }}
            >
                <ProductIntegrationWrapper>
                    <Stepper title={isEditMode ? "Edit Integration" : "Product Integration"}>
                        <div title="Customer & Product Details">
                            <CustomerAndProductDetails />
                        </div>
                        <div title="Payment Merchant">
                            <MerchantConfiguration />
                        </div>
                        <div title="Transaction Charge">
                            <TransactionCharge />
                        </div>
                        <div title="Upload Payment Form">
                            <UploadPaymentForm />
                        </div>
                        <div title="Summary">
                            <IntegrationSummary />
                        </div>
                    </Stepper>
                </ProductIntegrationWrapper>
            </ProductIntegrationContext.Provider>
        </Loadable>
    );
}
